import React, { useCallback, useRef, useState } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonButton,
  IonContent,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonToggle,
} from '@ionic/react';
import {
  chevronBack,
  notificationsOutline,
  phonePortraitOutline,
  volumeHighOutline,
  phonePortraitSharp,
  mailOutline,
  chatbubbleEllipsesOutline,
  personOutline,
  alertCircleOutline
} from 'ionicons/icons';
import styles from './InboxNotificationsSettingsModal.module.scss';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import { useTranslation } from 'react-i18next';

export interface InboxNotificationsSettingsModalProps {
  isOpen?: boolean;
  triggerId?: string;
  onClose?: () => void;
  onBack?: () => void;
}

const STORAGE_KEYS = {
  ENABLED: 'inbox.notifications.enabled',
  PUSH: 'inbox.notifications.push',
  EMAIL: 'inbox.notifications.email',
  SOUND: 'inbox.notifications.sound',
  VIBRATION: 'inbox.notifications.vibration',
  NEW_MESSAGES: 'inbox.notifications.newMessages',
  ASSIGNED_TO_ME: 'inbox.notifications.assignedToMe',
  MENTIONS: 'inbox.notifications.mentions',
  URGENT_MESSAGES: 'inbox.notifications.urgentMessages',
};

const InboxNotificationsSettingsModal: React.FC<InboxNotificationsSettingsModalProps> = ({ isOpen = false, triggerId, onClose = () => {}, onBack }) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLIonModalElement | null>(null);
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const containerRef = useRef<HTMLIonGridElement>(null);
  const [isPresented, setIsPresented] = useState(false);

  // Master toggle
  const [enabled, setEnabled] = useState(true);

  // Channel preferences
  const [pushEnabled, setPushEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);

  // Message type preferences
  const [newMessages, setNewMessages] = useState(true);
  const [assignedToMe, setAssignedToMe] = useState(true);
  const [mentions, setMentions] = useState(true);
  const [urgentMessages, setUrgentMessages] = useState(true);

  const dismiss = () => {
    modalRef.current?.dismiss();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      dismiss();
    }
  };

  // Reload all settings from localStorage whenever the modal opens —
  // computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside an effect.
  const modalActive = triggerId ? isPresented : isOpen;
  const [prevModalActive, setPrevModalActive] = useState(modalActive);
  if (prevModalActive !== modalActive) {
    setPrevModalActive(modalActive);
    if (modalActive) {
      const loadSetting = (key: string, defaultValue: boolean = true): boolean => {
        const raw = localStorage.getItem(key);
        return raw === null ? defaultValue : raw === '1';
      };

      setEnabled(loadSetting(STORAGE_KEYS.ENABLED));
      setPushEnabled(loadSetting(STORAGE_KEYS.PUSH));
      setEmailEnabled(loadSetting(STORAGE_KEYS.EMAIL, false));
      setSoundEnabled(loadSetting(STORAGE_KEYS.SOUND));
      setVibrationEnabled(loadSetting(STORAGE_KEYS.VIBRATION));
      setNewMessages(loadSetting(STORAGE_KEYS.NEW_MESSAGES));
      setAssignedToMe(loadSetting(STORAGE_KEYS.ASSIGNED_TO_ME));
      setMentions(loadSetting(STORAGE_KEYS.MENTIONS));
      setUrgentMessages(loadSetting(STORAGE_KEYS.URGENT_MESSAGES));
    }
  }

  const persist = (key: string, value: boolean, setter: (val: boolean) => void) => {
    setter(value);
    localStorage.setItem(key, value ? '1' : '0');
  };

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

  return (
    <IonModal
      ref={modalRef}
      {...(triggerId ? { trigger: triggerId } : { isOpen })}
      onDidPresent={() => setIsPresented(true)}
      onDidDismiss={() => {
        setIsPresented(false);
        onClose();
      }}
    >
      <IonPage>
        <BasePageWithHeader
          ref={baseHeaderRef}
          title={t('components.InboxNotificationsSettingsModal.inboxNotifications')}
          toolbarClassName={styles.toolbar}
          smallTitleClassName={styles.smallTitle}
          titleHeaderClassName={styles.titleHeader}
          startButtons={
            <IonButton
              shape="round"
              onClick={handleBack}
              aria-label={t('components.InboxNotificationsSettingsModal.backToInboxSettings')}
              className={styles.backButton}
            >
              <IonIcon icon={chevronBack} />
            </IonButton>
          }
          endButtons={
            <IonButton
              shape="round"
              className={styles.hiddenButton}
              style={{ visibility: 'hidden', pointerEvents: 'none' }}
            >
              <IonIcon icon={chevronBack} />
            </IonButton>
          }
        >
          <IonContent scrollY={false} className={styles.pageContent}>
            <IonGrid
              ref={containerRef}
              className={styles.settingsContainer}
              onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
            >
              <TitleCard
                ref={titleCardAnimationRef}
                title={t('components.InboxNotificationsSettingsModal.inboxNotifications')}
                subtitle={t('components.InboxNotificationsSettingsModal.controlHowAndWhenInboxAlerts')}
                subtitleClassName={styles.heroSubtitle}
                cardClassName={styles.settingsTitleCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.settingsTitle}
              />

              <div className={styles.settingsPanel}>
                <p className={styles.sectionEyebrow}>{t('components.InboxNotificationsSettingsModal.global')}</p>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem className={styles.masterToggleItem} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={notificationsOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.inboxNotifications')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.enableAllInboxNotifications')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={enabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.ENABLED, !!e.detail.checked, setEnabled)}
                    />
                  </IonItem>
                </IonList>
              </div>

              <div className={styles.settingsPanel}>
                <p className={styles.sectionEyebrow}>{t('components.InboxNotificationsSettingsModal.notificationChannels')}</p>
                <p className={styles.sectionDescription}>{t('components.InboxNotificationsSettingsModal.chooseHowYouWantToReceive')}</p>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem className={`${styles.settingsItem} ${!enabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${enabled ? styles.iconBadgePrimary : styles.iconBadgeMuted}`}>
                      <IonIcon icon={phonePortraitOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.pushNotifications')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.receiveNotificationsOnThisDevice')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={pushEnabled}
                      disabled={!enabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.PUSH, !!e.detail.checked, setPushEnabled)}
                    />
                  </IonItem>

                  <IonItem className={`${styles.settingsItem} ${!enabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${enabled ? styles.iconBadgePrimary : styles.iconBadgeMuted}`}>
                      <IonIcon icon={mailOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.emailNotifications')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.receiveNotificationsViaEmail')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={emailEnabled}
                      disabled={!enabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.EMAIL, !!e.detail.checked, setEmailEnabled)}
                    />
                  </IonItem>
                </IonList>
              </div>

              <div className={styles.settingsPanel}>
                <p className={styles.sectionEyebrow}>{t('components.InboxNotificationsSettingsModal.alertPreferences')}</p>
                <p className={styles.sectionDescription}>{t('components.InboxNotificationsSettingsModal.customizeHowAlertsAppearAndSound')}</p>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem className={`${styles.settingsItem} ${!enabled || !pushEnabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${(enabled && pushEnabled) ? styles.iconBadgePrimary : styles.iconBadgeMuted}`}>
                      <IonIcon icon={volumeHighOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.sound')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.playSoundForNewMessages')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={soundEnabled}
                      disabled={!enabled || !pushEnabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.SOUND, !!e.detail.checked, setSoundEnabled)}
                    />
                  </IonItem>

                  <IonItem className={`${styles.settingsItem} ${!enabled || !pushEnabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${(enabled && pushEnabled) ? styles.iconBadgePrimary : styles.iconBadgeMuted}`}>
                      <IonIcon icon={phonePortraitSharp} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.vibration')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.vibrateForNewMessages')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={vibrationEnabled}
                      disabled={!enabled || !pushEnabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.VIBRATION, !!e.detail.checked, setVibrationEnabled)}
                    />
                  </IonItem>
                </IonList>
              </div>

              <div className={styles.settingsPanel}>
                <p className={styles.sectionEyebrow}>{t('components.InboxNotificationsSettingsModal.messageTypes')}</p>
                <p className={styles.sectionDescription}>{t('components.InboxNotificationsSettingsModal.chooseWhichTypesOfInboxMessages')}</p>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem className={`${styles.settingsItem} ${!enabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${enabled ? styles.iconBadgePrimary : styles.iconBadgeMuted}`}>
                      <IonIcon icon={chatbubbleEllipsesOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.newMessages')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.allNewInboxMessages')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={newMessages}
                      disabled={!enabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.NEW_MESSAGES, !!e.detail.checked, setNewMessages)}
                    />
                  </IonItem>

                  <IonItem className={`${styles.settingsItem} ${!enabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${enabled ? styles.iconBadgeSecondary : styles.iconBadgeMuted}`}>
                      <IonIcon icon={personOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.assignedToMe')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.messagesAssignedToYou')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={assignedToMe}
                      disabled={!enabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.ASSIGNED_TO_ME, !!e.detail.checked, setAssignedToMe)}
                    />
                  </IonItem>

                  <IonItem className={`${styles.settingsItem} ${!enabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${enabled ? styles.iconBadgeTertiary : styles.iconBadgeMuted}`}>
                      <IonIcon icon={mailOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.mentions')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.whenYouReMentionedInA')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={mentions}
                      disabled={!enabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.MENTIONS, !!e.detail.checked, setMentions)}
                    />
                  </IonItem>

                  <IonItem className={`${styles.settingsItem} ${!enabled ? styles.disabled : ''}`} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${enabled ? styles.iconBadgeWarning : styles.iconBadgeMuted}`}>
                      <IonIcon icon={alertCircleOutline} />
                    </div>
                    <IonLabel className={styles.settingLabel}>
                      <h2>{t('components.InboxNotificationsSettingsModal.urgentMessages')}</h2>
                      <p>{t('components.InboxNotificationsSettingsModal.highPriorityAndUrgentMessages')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={urgentMessages}
                      disabled={!enabled}
                      onIonChange={(e) => persist(STORAGE_KEYS.URGENT_MESSAGES, !!e.detail.checked, setUrgentMessages)}
                    />
                  </IonItem>
                </IonList>
              </div>

              <div className={styles.scrollSpacer} />
            </IonGrid>
          </IonContent>
        </BasePageWithHeader>
      </IonPage>
    </IonModal>
  );
};

export default InboxNotificationsSettingsModal;
